import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { SECTORS } from "../lib/brand";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} />;
};

export const SectorsSection = () => {
  const [active, setActive] = useState(0);
  const sector = SECTORS[active];

  return (
    <section
      id="sectors"
      data-testid="sectors-section"
      className="relative bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00A3E0]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-600">
                Sectors We Serve
              </span>
            </div>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
              Cross-sector fluency.
              <br />
              <span className="text-[#00A3E0]">Single execution discipline.</span>
            </h2>
          </div>
          <p className="max-w-md text-sm text-slate-600">
            Hover or tap a sector to preview how CPS structures consortium
            engagements for it.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* Sector list */}
          <div className="lg:col-span-5">
            <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {SECTORS.map((s, i) => {
                const isActive = i === active;
                return (
                  <li key={s.name}>
                    <button
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                      onClick={() => setActive(i)}
                      data-testid={`sector-row-${i}`}
                      className={`group flex w-full items-center justify-between px-6 py-5 text-left transition-colors ${
                        isActive ? "bg-[#00A3E0]/[0.06]" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span
                          className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
                            isActive
                              ? "bg-[#00A3E0] text-white"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          <Icon name={s.icon} className="h-5 w-5" />
                        </span>
                        <div>
                          <p className="font-display text-lg font-bold text-slate-900">
                            {s.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            0{i + 1} / 0{SECTORS.length}
                          </p>
                        </div>
                      </div>
                      <Icons.ArrowUpRight
                        className={`h-5 w-5 transition-all ${
                          isActive
                            ? "translate-x-0 text-[#00A3E0] opacity-100"
                            : "-translate-x-1 text-slate-300 opacity-0 group-hover:opacity-100"
                        }`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Sector preview */}
          <div className="relative lg:col-span-7">
            <div className="relative aspect-[5/4] w-full overflow-hidden rounded-2xl bg-slate-100">
              <AnimatePresence mode="wait">
                <motion.img
                  key={sector.image}
                  src={sector.image}
                  alt={sector.name}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={sector.name}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#00A3E0]">
                      Sector
                    </p>
                    <h3 className="mt-1 font-display text-3xl font-extrabold text-white sm:text-4xl">
                      {sector.name}
                    </h3>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85">
                      {sector.blurb}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectorsSection;
